<template>
    <SectionBase v-bind="$attrs" :section="section" >
        <div class="testimonial" :class="controlThemeClass">
            <div ref="slider" class="ufo--slider">
                <div class="testimonial-slider__stage" :class="{'testimonial-card': hasCardBackground}" :style="cardStyle">
                    <div class="ufo--slider-track slider__track" data-glide-el="track" style="overflow:hidden;">
                        <ul :class="['ufo--slides carousel__slides--slider', {'is-slideable': section.testimonial.length > 1}]">
                            <li v-for="(quote, index) in section.testimonial" class="ufo--slide" :key="`quote-${index}`">
                                <AnimationWrapper>
                                    <template #animationContent>
                                        <GetColumnMediaContent v-if="section.contentSettings?.showMedia" :column-media="quote.media" :placeholder-image="'icon-user-picture-empty-state.svg'" />
                                    </template>
                                </AnimationWrapper>
                                <AnimationWrapper>
                                    <template #animationContent>
                                        <div :class="['quote', quote.text.style?.textStyle]" v-html="getQuote(quote.text.text)"></div>
                                    </template>
                                </AnimationWrapper>
                                <AnimationWrapper>
                                    <template #animationContent>
                                        <div :class="['author', quote.author.style?.textStyle]" v-html="getQuote(quote.author.text)"></div>
                                    </template>
                                </AnimationWrapper>
                            </li>
                        </ul>
                    </div>
                    <GalleryArrowButtons
                        v-if="showControls && controlsPosition === C.SIDES"
                        :control-position="controlsPosition"
                        item-label="testimonial"
                        @next="scrollNext"
                        @previous="scrollPrevious"
                    />
                </div>
                <AnimationWrapper>
                    <template #animationContent>
                        <GalleryControls
                            v-if="showControls"
                            :items-length="section.testimonial.length"
                            :scroll-snaps="section.testimonial"
                            :selected-scroll-snap="activeSlideIndex"
                            :controls-position="controlsPosition"
                            item-label="testimonial"
                            @next="scrollNext"
                            @previous="scrollPrevious"
                            @scroll-to="navToSlide"
                        />
                    </template>
                </AnimationWrapper>
            </div>
        </div>
    </SectionBase>
</template>

<script>
    import SectionBase from "./SectionBase.vue";
    import GetColumnMediaContent from "./_helper/GetColumnMediaContent.vue";
    import Glide from "@glidejs/glide";
    import AnimationWrapper from "../../../common/components/AnimationWrapper.vue";
    import GalleryControls from "@/scripts/sections/modules/gallery/GalleryControls.vue";
    import GalleryArrowButtons from "@/scripts/sections/modules/gallery/GalleryArrowButtons.vue";
    import {GalleryControlsPosition} from "@/scripts/common/enums.js";
    import {mapGetters} from "vuex";
    import {resolveColor} from "@/scripts/common/helpers.js";
    import {getControlThemeClass} from "@/scripts/common/utilities/colorUtils.js";

    export default {
        name: "TestimonialSection",
        components: {
            SectionBase,
            AnimationWrapper,
            GetColumnMediaContent,
            GalleryControls,
            GalleryArrowButtons
        },
        props: ['section'],
        data() {
            return {
                slider: null,
                activeSlideIndex: 0,
                C: GalleryControlsPosition
            }
        },
        mounted() {
            if(this.section.testimonial.length > 1) this.mountSlider(0);
        },
        unmounted() {
            this.destroySlider();
        },
        computed: {
            ...mapGetters("design", ["colorPalette", "resolveColor"]),
            showControls() {
                return this.section.testimonial.length > 1;
            },
            controlsPosition() {
                return this.section?.controlsPosition ?? GalleryControlsPosition.BOTTOM;
            },
            controlThemeClass() {
                return getControlThemeClass(
                    this.section?.backgroundSettings?.backgroundColor || this.resolveColor("background"),
                    this.colorPalette
                );
            },
            hasCardBackground() {
                const color = this.section?.stageBackgroundColor;
                return !!color?.color || color?.paletteColorIndex != null;
            },
            cardStyle() {
                const style = {
                    "--testimonial-card-radius": `${this.section?.cornerRadiusPx ?? 0}px`
                };
                if (!this.hasCardBackground) return style;

                return {
                    ...style,
                    backgroundColor: resolveColor(this.section.stageBackgroundColor, this.colorPalette)
                };
            }
        },
        methods: {
            getQuote(text) {
                return text.replaceAll('\n', '<br />')
            },
            mountSlider(position) {
                if(!!this.slider) {
                    this.$nextTick(() => this.slider.update({ startAt: position }));
                    return;
                }
                this.slider = new Glide(this.$refs.slider, {
                    slidesToShow: 1,
                    slidesToScroll: 1,
                    startAt: position,
                    keyboard: true,
                    perView: 1,
                    animationDuration: 300,
                    focusAt: 'center',
                    dragThreshold: 120
                });
                this.slider.on('run.after', () => {
                    this.activeSlideIndex = this.slider.index;
                });
                this.slider.mount();
            },
            navToSlide(slide) {
                return this.slider.go(`=${slide}`)
            },
            scrollPrevious() {
                this.slider?.go('<');
            },
            scrollNext() {
                this.slider?.go('>');
            },
            destroySlider() {
                if(!!this.slider) {
                    this.slider.destroy();
                    this.slider = null;
                }
            }
        }
    }
</script>

<style lang="scss" scoped>
    .is-slideable .ufo--slide {
        cursor: grab;
    }
</style>